import { useEffect, useState } from "react";

/**
 * "What's new" — every in-progress ET change behind its own switch.
 *
 * Isaac wants to look at each of these on the live site and keep or kill them one at a time,
 * so nothing here changes the editor until it is turned on. Every flag defaults to OFF: a user
 * who never opens Settings sees exactly the ET they saw yesterday.
 *
 * Stored per browser like the other ET prefs (`cc_et_view`, `cc_et_mode`) and reactive through
 * the same event pattern, so flipping a switch redraws the editor without a reload.
 */
const KEY = "cc_et_labs";
export const LABS_EVENT = "cc-et-labs-changed";

/** The switches, in the order they appear in Settings. `name` is what the switch says. */
export const LABS = [
  {
    key: "sharePill",
    name: "Share button",
    hint: "Tick exactly ONE clip and Share appears beside Play. On a phone it hands that clip to WhatsApp, Instagram or anywhere; on a laptop it saves it.",
  },
  {
    key: "postFlow",
    name: "Post to social",
    hint: "Needs the Share button on too. Share then opens a box with the platforms, your caption and the spreadsheet link.",
  },
  {
    key: "progress",
    name: "Progress that stays put",
    hint: "A ring that lasts until the clip is actually ready, instead of a message that vanishes.",
  },
  {
    key: "quietRail",
    name: "Quieter tool bubbles",
    hint: "Smaller at rest on one tray, and they grow when you point at them.",
  },
  {
    key: "tidyTimes",
    name: "Tidier timecode buttons",
    hint: "Makes every \u2212 and + an identical square so the two rows line up. No labels \u2014 they only made it busier.",
  },
  {
    key: "timelineTab",
    name: "Timeline tab always there",
    hint: "Stops the timeline tab being something you switch on in Settings first.",
  },
  {
    key: "toggleLeft",
    name: "Move Phone / Full left",
    hint: "It currently sits on top of the last row's timecodes and download arrow.",
  },
];

const KNOWN = LABS.map((l) => l.key);

/**
 * What a flag is when the user has never touched it.
 *
 * OFF, deliberately: with every switch off the editor must be indistinguishable from the one
 * that shipped yesterday, so merging this can change nothing for anyone until Isaac turns a
 * switch on and judges it on the live site. Flip these to true to make a change the default.
 */
const DEFAULTS = KNOWN.reduce((acc, k) => { acc[k] = false; return acc; }, {});

function read() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || "{}");
    const out = {};
    // An explicit boolean wins; anything unset falls back to the default.
    KNOWN.forEach((k) => { out[k] = typeof raw[k] === "boolean" ? raw[k] : DEFAULTS[k]; });
    return out;
  } catch (e) {
    return { ...DEFAULTS };
  }
}

/** Every flag as { key: boolean }. Unknown or corrupt storage reads as all-off. */
export function getLabs() {
  return read();
}

/** One flag. */
export function getLab(key) {
  return read()[key] === true;
}

/** Flip one flag and tell every mounted ET about it. */
export function setLab(key, on) {
  if (KNOWN.indexOf(key) === -1) return;
  const next = read();
  next[key] = !!on;
  try { localStorage.setItem(KEY, JSON.stringify(next)); } catch (e) { /* private window */ }
  try { window.dispatchEvent(new CustomEvent(LABS_EVENT, { detail: next })); } catch (e) { /* ignore */ }
}

/** Put every switch back to how it ships. */
export function resetLabs() {
  try { localStorage.removeItem(KEY); } catch (e) { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent(LABS_EVENT, { detail: read() })); } catch (e) { /* ignore */ }
}

/** How many are on — so Settings can show a count without reading storage itself. */
export function labsOnCount() {
  const l = read();
  return KNOWN.filter((k) => l[k]).length;
}

/** Reactive flags. Re-renders whenever any switch moves, anywhere. */
export function useLabs() {
  const [labs, setLabs] = useState(read);
  useEffect(() => {
    const on = (e) => setLabs(e && e.detail ? e.detail : read());
    window.addEventListener(LABS_EVENT, on);
    return () => window.removeEventListener(LABS_EVENT, on);
  }, []);
  return labs;
}
